import React, { useState, useEffect, useContext } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import AlmacenesService from "../../../services/almacenes.service";
import UsuariosService from "../../../services/usuarios.service";
import ProductService from "../../../services/product.service";
import TraspasosService from "../api/traspasos.api";
import Toasts from "../../../components/notificacion/Toasts";
import { StateContext } from "../../../components/context/StateProvider";

import { TraspasoForm } from "../components/forms";
import { getStockEnAlmacen } from "../helpers/traspasoForm";

/**
 * Página para crear/editar traspasos entre almacenes
 */
export default function TraspasoAlmacenes() {
    const { state, updateState } = useContext(StateContext);
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(!!id);
    const [almacenesOptions, setAlmacenesOptions] = useState([]);
    const [empleadosOptions, setEmpleadosOptions] = useState([]);
    const [traspaso, setTraspaso] = useState(null);

    useEffect(() => {
        cargarDatos();
    }, [id]);

    const cargarDatos = async () => {
        try {
            // Cargar catálogos
            const [almacenes, empleados] = await Promise.all([
                AlmacenesService.obtenerAlmacenes(true),
                UsuariosService.listarUsuarios(),
            ]);

            setAlmacenesOptions(
                (almacenes ?? []).map((a) => ({
                    value: a.CODIGO,
                    label: `${a.CODIGO} - ${a.NOMBRE}`,
                }))
            );

            setEmpleadosOptions(
                (empleados ?? []).map((e) => ({
                    value: e.id,
                    label: e.nombre || e.username,
                }))
            );

            // Si es edición, cargar traspaso
            if (id) {
                const traspasoData = await TraspasosService.getById(id);
                const cab = traspasoData.cabecera ?? traspasoData;
                const lineasTraspaso = traspasoData.lineas ?? traspasoData.Lineas ?? [];

                // Cargar stock para cada línea
                const lineasConStock = await Promise.all(
                    lineasTraspaso.map(async (l) => {
                        const codigo = l.codigoArticulo ?? l.CODIGO ?? l.ARTICULO;
                        const nombre = l.nombre ?? l.NOMBRE ?? "";
                        const barras = l.barras ?? l.BARRAS ?? "";
                        const unidades = Number(l.unidades ?? l.UNIDADES ?? 0);

                        try {
                            const productos = await ProductService.getProducts(codigo);
                            const producto = productos?.[0];

                            const stockOrigen = producto
                                ? getStockEnAlmacen(producto, cab.almacenOrigen ?? cab.origen)
                                : 0;
                            const stockDestino = producto
                                ? getStockEnAlmacen(producto, cab.almacenDestino ?? cab.destino)
                                : 0;

                            return {
                                id: codigo,
                                codigo,
                                nombre,
                                barras,
                                stockOrigen,
                                stockDestino,
                                unidades,
                            };
                        } catch {
                            return {
                                id: codigo,
                                codigo,
                                nombre,
                                barras,
                                stockOrigen: 0,
                                stockDestino: 0,
                                unidades,
                            };
                        }
                    })
                );

                setTraspaso({
                    id: traspasoData.id,
                    almacenOrigen: traspasoData.almacenOrigen,
                    almacenDestino: traspasoData.almacenDestino,
                    empleadoOrigenId: traspasoData.empleadoOrigenId,
                    empleadoDestinoId: traspasoData.empleadoDestinoId,
                    observaciones: traspasoData.observaciones ?? "",
                    lineas: lineasConStock,
                });
            }
        } catch (error) {
            console.error("Error cargando datos:", error);
            mostrarError("Error al cargar los datos del formulario");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (payload, action) => {
        try {
            if (action === 'updated') {
                await TraspasosService.update(id, payload);
                mostrarExito("Traspaso actualizado correctamente");
            } else {
                await TraspasosService.create(payload);
                mostrarExito("Traspaso creado correctamente");
            }
            navigate("/traspasos");
        } catch (error) {
            throw error; // Re-lanzar para que el form lo maneje
        }
    };

    const handleError = (mensaje) => {
        mostrarError(mensaje);
    };

    const handleCancel = () => {
        navigate("/traspasos");
    };

    const mostrarExito = (mensaje) => {
        const toast = Toasts.generateToast(mensaje, "success");
        updateState({ toasts: [...state.toasts, toast] });
    };

    const mostrarError = (mensaje) => {
        const toast = Toasts.generateToast(mensaje, "error");
        updateState({ toasts: [...state.toasts, toast] });
    };

    // Loading state
    if (loading) {
        return (
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "400px",
                }}
            >
                <Box sx={{ textAlign: "center" }}>
                    <CircularProgress sx={{ color: "#cc071e", mb: 2 }} />
                    <Typography sx={{ color: "#6c757d" }}>Cargando...</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <TraspasoForm
                traspaso={traspaso}
                almacenesOptions={almacenesOptions}
                empleadosOptions={empleadosOptions}
                onSave={handleSave}
                onError={handleError}
                onCancel={handleCancel}
            />
        </Box>
    );
}
